/**
 * Metodo utilizado para formatar o caminho percorrido na simulação de forma que retorne apenas um caminho para simulara palavra
 * Utilizado no componente SimulateAutomatonWord
 * @typedef {Object} TapeStep
 * @property {Object} actualState - O estado atual
 * @property {string} parent - O nome do estado pai
 * @property {number} pos - Posição do ponteiro na fita (índice de consumo).
 * @property {number} numberComputations - Número da computação/profundidade na busca.
 */

/**
 * Filtra o array de histórico da simulação (tapeSteps) para extrair
 * o caminho que resultou na aceitação ou o caminho que
 * consumiu a maior parte da palavra.
 *
 * @param {Array<TapeStep>} tapeSteps - O histórico completo da simulação com todas as bifurcações.
 * @param {boolean} isAccept - Indica se a palavra foi aceita na simulação geral.
 * @param {Array<Object>} finalStates - Lista de estados de aceitação da máquina.
 * @returns {Array<TapeStep>} O caminho linear mais longo ou da aceitação.
 */
export function formatPath(tapeSteps, isAccept, finalStates) {
  if (!tapeSteps || tapeSteps.length === 0) {
    return [];
  }

  // Lista de nomes de estados finais para checagem rápida
  const finalStateNames = finalStates.map((s) => s.name);

  let bestEndNode = null;
  let maxPos = -1;

  // 1. Encontrar o 'Melhor Nó Final' (BestEndNode)
  tapeSteps.forEach((step) => {
    if (isAccept) {
      const isFinal = finalStateNames.includes(step.actualState.name);
      const consumedWord = step.pos;

      if (isFinal && consumedWord >= maxPos) {
        maxPos = consumedWord;
        bestEndNode = step;
      }
    }

    if (!isAccept && step.pos >= maxPos) {
      maxPos = step.pos;
      bestEndNode = step;
    }
  });

  if (!bestEndNode) {
    return tapeSteps.slice(0, 1);
  }

  // 2. Rastreamento do Caminho (Backtracking)
  let path = [bestEndNode];
  let currentStep = bestEndNode;

  // Mapeamento para busca reversa: (estado_atual + numberComputations) -> passo
  const stepMap = new Map();
  tapeSteps.forEach((step) => {
    stepMap.set(`${step.actualState.name}|${step.numberComputations}`, step);
  });

  // Rastreia até o estado inicial (comp=0)
  while (currentStep.numberComputations > 0) {
    const parentKey = `${currentStep.parent}|${currentStep.numberComputations - 1}`;
    const parentStep = stepMap.get(parentKey);

    if (parentStep) {
      path.push(parentStep);
      currentStep = parentStep;
    } else {
      console.warn(
        `Caminho quebrado: Não foi possível encontrar o pai para ${currentStep.actualState.name} na comp ${currentStep.numberComputations}.`
      );
      break;
    }
  }

  return path.reverse();
}
